import { useState } from "react";
import axios from "axios";
import { Search, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const R = 6371000;
const distM = (a, b) => {
  const r = (d) => (d * Math.PI) / 180;
  const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
const fill = (s, v) => s.replace(/\{(\w+)\}/g, (_, k) => v[k]);

export const checkCoverage = (point, hubs) => {
  const active = hubs.filter((h) => h.status === "active");
  if (!active.length) return null;
  const near = active.map((h) => ({ h, d: distM(point, h) })).sort((a, b) => a.d - b.d)[0];
  return { hub: near.h, d: near.d, covered: near.d <= near.h.radius_m };
};

export const AddressSearch = ({ s, hubs, lang, onFound }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const toForm = () => {
    const addr = res?.query || q.trim();
    window.dispatchEvent(new CustomEvent("lead-prefill", { detail: { city: /а[кқ]тау/i.test(addr) ? addr : `${s.city}, ${addr}` } }));
    scrollToId("contacts");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/geocode`, { params: { q: q.trim() } });
      const point = { lat: data?.lat, lng: data?.lng };
      if (point.lat == null) throw new Error("bad");
      onFound(point);
      setRes({ point, precision: data.precision, cov: checkCoverage(point, hubs), query: q.trim() });
    } catch {
      setRes({ notFound: true });
    } finally {
      setBusy(false);
    }
  };

  const name = (h) => (lang === "kz" ? h.name_kz : h.name_ru);
  const cov = res?.cov;

  return (
    <div className="mb-5" data-testid="netmap-search">
      <form onSubmit={submit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--gold)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} maxLength={120} placeholder={s.placeholder} aria-label={s.placeholder}
            className="w-full rounded-full border border-[var(--gold)]/40 bg-[#0F131C] pl-11 pr-4 py-3 text-sm md:text-base text-[var(--text)] placeholder:text-[var(--muted)] outline-none focus:border-[var(--gold)] transition-colors"
            data-testid="netmap-search-input" />
        </div>
        <button disabled={busy} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[var(--gold)] px-5 md:px-7 text-sm font-semibold text-[#0A0D14] hover:brightness-110 transition-[filter] disabled:opacity-70" data-testid="netmap-search-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}<span className="hidden sm:inline">{busy ? s.loading : s.btn}</span>
        </button>
      </form>
      {res && (
        <div className={`mt-3 rounded-2xl border px-5 py-4 ${cov?.covered ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-white/15 bg-[#0F131C]"}`} data-testid="netmap-search-result">
          {res.notFound && <p className="text-sm text-[var(--text)]" data-testid="netmap-search-notfound">{s.notFound}</p>}
          {!res.notFound && (
            <div className="flex items-start gap-3">
              {cov?.covered ? <CheckCircle2 className="w-5 h-5 text-[var(--gold)] shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-[var(--muted)] shrink-0 mt-0.5" />}
              <div className="flex-1">
                <p className="font-display text-base text-[var(--text)]" data-testid="netmap-search-status">{cov?.covered ? s.covered : s.none}</p>
                {cov && <p className="mt-1 text-sm text-[var(--muted)]">{cov.covered ? fill(s.coveredText, { hub: name(cov.hub), d: Math.round(cov.d / 10) * 10 }) : fill(s.noneText, { hub: name(cov.hub), d: (cov.d / 1000).toLocaleString("ru-RU", { maximumFractionDigits: 1 }) })}</p>}
                {res.precision === "md" && <p className="mt-1 text-xs text-[var(--muted)]" data-testid="netmap-search-approx">{s.approx}</p>}
                <button onClick={toForm} className="mt-3 inline-flex rounded-full border border-[var(--gold)] px-4 py-1.5 text-sm text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[#0A0D14] transition-colors" data-testid="netmap-search-cta">{cov?.covered ? s.ctaConnect : s.cta}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
